import React, { useState, useCallback } from "react";
import axios from "axios";
import { TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RefreshCw, Key, Eye, EyeOff, Trash2, Check, Wifi, CheckCircle2, XCircle, Activity, AlertTriangle } from "lucide-react";
import { useAuth } from "../../AuthContext";
import { BACKEND_URL } from "../../lib/backend";

const API = `${BACKEND_URL}/api`;

const HEALTH_PROVIDER_LABELS = {
  openai: "OpenAI",
  brevo: "Brevo",
  perplexity: "Perplexity",
  firecrawl: "Firecrawl",
};

const STATUS_STYLE = {
  ok:       { light: "bg-emerald-50 text-emerald-700",  dark: "bg-emerald-400/10 text-emerald-400" },
  missing:  { light: "bg-amber-50 text-amber-700",      dark: "bg-amber-400/10 text-amber-400" },
  invalid:  { light: "bg-red-50 text-red-700",          dark: "bg-red-400/10 text-red-400" },
  quota:    { light: "bg-orange-50 text-orange-700",    dark: "bg-orange-400/10 text-orange-400" },
  error:    { light: "bg-red-50 text-red-700",          dark: "bg-red-400/10 text-red-400" },
};

export default function ApiKeysTab({
  dm, apiKeys, keyValues, setKeyValues,
  showKey, setShowKey, savingKeys, loadingKeys,
  testResults, setTestResults,
  saveApiKeys, removeApiKey, testApiKey,
}) {
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);

  const { authHeader } = useAuth();

  const runHealthCheck = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const res = await axios.get(`${API}/admin/health/secrets`, { headers: authHeader(), timeout: 30000 });
      setHealthData(res.data);
    } catch (err) {
      setHealthError(err?.response?.data?.detail || err.message || "Health check failed");
    } finally {
      setHealthLoading(false);
    }
  }, []);

  return (
    <TabsContent value="apikeys">
      <div className="max-w-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>API Key Management</h2>
            <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Keys are encrypted with AES-256 (Fernet) and stored securely in MongoDB. Leave a field empty to keep the existing value.</p>
          </div>
          <button
            onClick={runHealthCheck}
            disabled={healthLoading}
            data-testid="run-secrets-healthcheck-btn"
            className={`flex-shrink-0 px-3 py-2 rounded-full category-tag transition-all flex items-center gap-1.5 ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#e0f2f1] text-[#006874] hover:bg-[#b2dfdb]"}`}
            title="Live ping each third-party provider with the current key stored in this pod's DB"
          >
            {healthLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
            <span>Live health check</span>
          </button>
        </div>

        {(healthData || healthError) && (
          <div
            className={`rounded-2xl p-5 mb-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
            style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}
            data-testid="secrets-healthcheck-results"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                <span className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Live secrets status</span>
                {healthData && (
                  <span className={`px-2 py-0.5 rounded-full category-tag ${
                    healthData.overall === "ok"
                      ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700")
                      : (dm ? "bg-amber-400/10 text-amber-400" : "bg-amber-50 text-amber-700")
                  }`}>
                    {healthData.overall}
                  </span>
                )}
              </div>
              {healthData && (
                <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                  {new Date(healthData.checked_at * 1000).toLocaleTimeString()}
                </span>
              )}
            </div>

            {healthError && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-700"}`}>
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{healthError}</span>
              </div>
            )}

            {healthData && (
              <div className="space-y-2">
                {healthData.checks.map((c) => {
                  const style = STATUS_STYLE[c.status] || STATUS_STYLE.error;
                  return (
                    <div
                      key={c.name}
                      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}
                      data-testid={`secrets-healthcheck-row-${c.name}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`px-2 py-0.5 rounded-full category-tag font-medium ${dm ? style.dark : style.light}`}>
                          {c.status}
                        </span>
                        <span className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                          {HEALTH_PROVIDER_LABELS[c.name] || c.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{c.detail}</span>
                        {typeof c.latency_ms === "number" && (
                          <span className={`category-tag font-mono ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                            {c.latency_ms}ms
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {loadingKeys ? (
          <div className="flex justify-center py-16"><RefreshCw className={`w-5 h-5 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
        ) : (
          <div className="space-y-5">
            {apiKeys.map((keyMeta) => (
              <div key={keyMeta.name} className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{keyMeta.label}</Label>
                      {keyMeta.is_set && (
                        <span className={`px-2 py-0.5 rounded-full category-tag flex items-center gap-1 ${keyMeta.source === "database" ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700") : (dm ? "bg-amber-400/10 text-amber-400" : "bg-amber-50 text-amber-700")}`}>
                          {keyMeta.source === "database" ? <><Check className="w-2.5 h-2.5" />DB</> : <>ENV</>}
                        </span>
                      )}
                      {!keyMeta.is_set && (
                        <span className={`px-2 py-0.5 rounded-full category-tag ${dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-600"}`}>Not set</span>
                      )}
                    </div>
                    <p className={`category-tag mt-0.5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{keyMeta.hint}</p>
                  </div>
                  {keyMeta.source === "database" && (
                    <button onClick={() => removeApiKey(keyMeta.name)} title="Remove from DB (revert to env)"
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${dm ? "text-slate-600 hover:bg-red-400/10 hover:text-red-400" : "text-[#e7e8e9] hover:bg-red-50 hover:text-red-500"}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {keyMeta.is_set && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 font-mono text-xs overflow-hidden ${dm ? "bg-[#0c1011] text-slate-500" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                    <span className="truncate">Current: {keyMeta.masked_value || "—"}</span>
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    type={showKey[keyMeta.name] ? "text" : "password"}
                    value={keyValues[keyMeta.name] || ""}
                    onChange={(e) => {
                      setKeyValues(prev => ({ ...prev, [keyMeta.name]: e.target.value }));
                      if (testResults[keyMeta.name]) setTestResults(prev => ({ ...prev, [keyMeta.name]: null }));
                    }}
                    placeholder={keyMeta.is_set ? "Enter new value to update…" : keyMeta.placeholder}
                    className={`border-0 font-mono text-sm ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
                    data-testid={`api-key-input-${keyMeta.name}`}
                  />
                  <button
                    onClick={() => testApiKey(keyMeta.name)}
                    disabled={testResults[keyMeta.name]?.status === "testing" || (!keyValues[keyMeta.name]?.trim() && !keyMeta.is_set)}
                    data-testid={`test-key-btn-${keyMeta.name}`}
                    className={`px-3 py-2.5 rounded-lg transition-colors flex items-center gap-1.5 flex-shrink-0 category-tag disabled:opacity-40 disabled:cursor-not-allowed ${dm ? "bg-[#252e2f] text-slate-400 hover:bg-[#303b3c] hover:text-slate-200" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e7e8e9] hover:text-[#191c1d]"}`}
                  >
                    {testResults[keyMeta.name]?.status === "testing" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">Test</span>
                  </button>
                  <button onClick={() => setShowKey(prev => ({ ...prev, [keyMeta.name]: !prev[keyMeta.name] }))}
                    className={`p-2.5 rounded-lg transition-colors flex-shrink-0 ${dm ? "bg-[#252e2f] text-slate-400 hover:bg-[#303b3c]" : "bg-[#f3f4f5] hover:bg-[#e7e8e9]"}`}>
                    {showKey[keyMeta.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {testResults[keyMeta.name] && testResults[keyMeta.name].status !== "testing" && (
                  <div className={`mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                    testResults[keyMeta.name].status === "success"
                      ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700")
                      : (dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-700")
                  }`}>
                    {testResults[keyMeta.name].status === "success"
                      ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                    <span>{testResults[keyMeta.name].message}</span>
                  </div>
                )}
              </div>
            ))}

            <div className={`rounded-2xl p-5 ${dm ? "bg-[#1a2122]/50 border border-white/5" : "bg-[#f3f4f5]/50 border border-black/5"}`}>
              <p className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                Keys stored in <strong>DB</strong> take priority over environment variables. Keys stored in <strong>ENV</strong> come from the server&apos;s .env file. Delete a DB key to revert to the ENV fallback.
              </p>
            </div>

            <button
              onClick={saveApiKeys}
              disabled={savingKeys || Object.values(keyValues).every(v => !v?.trim())}
              data-testid="save-api-keys-btn"
              className={`w-full py-3 rounded-full category-tag transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
            >
              {savingKeys ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Encrypting & Saving…</> : <><Key className="w-3.5 h-3.5" />Save Encrypted Keys</>}
            </button>

            <BillingSecretsSection dm={dm} authHeader={authHeader} />
          </div>
        )}
      </div>
    </TabsContent>
  );
}

const BILLING_SECRET_LABELS = {
  STRIPE_SECRET_KEY: "Stripe Secret Key",
  STRIPE_WEBHOOK_SECRET: "Stripe Webhook Secret",
  STRIPE_PRICE_PRO_MONTHLY: "Stripe Price — PRO Monthly",
  STRIPE_PRICE_PRO_YEARLY: "Stripe Price — PRO Yearly",
  REVENUECAT_STRIPE_PUBLIC_API_KEY: "RevenueCat Stripe Public API Key",
};

function BillingSecretsSection({ dm, authHeader }) {
  const [meta, setMeta] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/billing-secrets`, { headers: authHeader() });
      setMeta(res.data.secrets);
    } catch { setMeta([]); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(values).filter(([, v]) => v?.trim()));
    if (!Object.keys(payload).length) return;
    setSaving(true);
    setFeedback(null);
    try {
      const res = await axios.put(`${API}/admin/billing-secrets`, { keys: payload }, { headers: authHeader() });
      setFeedback({ ok: true, msg: `Rotated: ${res.data.saved_keys.join(", ")} — live within seconds, no redeploy.` });
      setValues({});
      load();
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || "Save failed" });
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-10" data-testid="billing-secrets-section">
      <h3 className={`editorial-heading text-xl font-medium mb-1 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Billing secrets</h3>
      <p className={`category-tag mb-5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
        Write-only: current values are never displayed. Billing code reads these per-request — rotations apply without redeploy.
      </p>
      <div className="space-y-4">
        {(meta || []).map((s) => (
          <div key={s.name} className={`rounded-2xl p-5 ${dm ? "bg-[#1a2122]" : "bg-white border border-[#eef0f0]"}`}>
            <div className="flex items-center justify-between mb-1.5">
              <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{BILLING_SECRET_LABELS[s.name] || s.name}</p>
              <span className={`px-2 py-0.5 rounded-full category-tag ${s.is_set ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700") : (dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-600")}`}>
                {s.is_set ? (s.source === "database" ? "DB" : "ENV") : "Not set"}
              </span>
            </div>
            <p className={`category-tag mb-3 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid={`billing-secret-meta-${s.name}`}>
              {s.is_set
                ? `Set${s.set_at ? ` on ${new Date(s.set_at).toLocaleString()}` : ""}${s.set_by ? ` by ${s.set_by}` : ""}${s.last4 ? ` · ••••${s.last4}` : ""}`
                : "Never set"}
            </p>
            <Input
              type="password"
              autoComplete="new-password"
              value={values[s.name] || ""}
              onChange={(e) => setValues(prev => ({ ...prev, [s.name]: e.target.value }))}
              placeholder="Enter new value to rotate…"
              data-testid={`billing-secret-input-${s.name}`}
              className={`border-0 font-mono text-sm ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] placeholder:text-[#bac9cc]"}`}
            />
          </div>
        ))}
      </div>
      {feedback && (
        <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium ${feedback.ok ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700") : (dm ? "bg-red-400/10 text-red-400" : "bg-red-50 text-red-700")}`}>
          {feedback.msg}
        </div>
      )}
      <button
        onClick={save}
        disabled={saving || Object.values(values).every(v => !v?.trim())}
        data-testid="save-billing-secrets-btn"
        className={`w-full mt-4 py-3 rounded-full category-tag transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
      >
        {saving ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Encrypting & Rotating…</> : <><Key className="w-3.5 h-3.5" />Rotate Billing Secrets</>}
      </button>
    </div>
  );
}
